import { BedType } from '@data'
import { useBed } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppSelector } from '@store/hooks'
import { palette } from '@themes'
import { Button, CheckBox, FormButton, Input, Modal, SelectInputField, TextAreaField } from '@ui'
import { getError, showToast } from '@util'
import { Box, DropButton, Form, Text } from 'grommet'
import { Checkmark, FormDown } from 'grommet-icons'
import { useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import useFormPersist from 'react-hook-form-persist'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

type Props = {
  onClose: () => void
  id?: string
}

export const bedStatus = [
  { name: 'Available', value: 'available' },
  { name: 'Not in service', value: 'not_in_service' }
]

export const bedSchema = yup.object({
  bedType: yup.string().required('Bed Type is required'),
  description: yup.string(),

  otherText: yup
    .string()
    .nullable()
    .when('bedType', ([bedType], schema) =>
      bedType === BedType.Other ? schema.required('Please provide other bed type name') : schema.notRequired()
    ),
  status: yup.string().required('Status is required'),
  telemetry: yup.boolean()
})

export const AddBed = ({ onClose, id: roomId }: Props) => {
  const { id } = useParams()
  const methods = useForm({ resolver: yupResolver(bedSchema) })
  const bedType = useAppSelector(state => state.config.bed_types)
  const [otherText, setOtherText] = useState<BedType>()
  const [showDrop, setShowDrop] = useState(false)
  const { createBed } = useBed({})

  const bedTypes = useMemo(() => {
    return [...bedType, 'Other']
  }, [bedType])

  const {
    watch,
    setValue,
    handleSubmit,
    control,
    reset,
    formState: { isSubmitting, errors }
  } = methods
  const { clear } = useFormPersist('addBed', { setValue, storage: window.localStorage, watch })

  const bed = watch('bedType')

  const handleClose = () => {
    onClose()
    reset()
    clear()
  }

  const onSubmit = async (data: yup.InferType<typeof bedSchema>) => {
    const payload: {
      data: {
        type: 'beds'
        attributes: {
          bed_type: string
          description?: string
          telemetry?: boolean
          status?: string
        }
        relationships: {
          room: {
            data: {
              id: string | number
              type: 'room'
            }
          }
        }
      }
    } = {
      data: {
        attributes: {
          bed_type: data.bedType,
          description: data.description,
          status: data.status.toLowerCase(),
          telemetry: data.telemetry
        },
        relationships: {
          room: {
            data: {
              id: id || roomId || '',
              type: 'room'
            }
          }
        },
        type: 'beds'
      }
    }

    if (data.status.toLowerCase() === 'available') {
      delete payload.data.attributes.status
    }
    try {
      await createBed(payload)
      showToast('Bed added successfully')
      handleClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={handleClose} title="Add Bed">
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Box overflow="auto" pad={{ horizontal: '32px', vertical: '16px' }} width={'530px'}>
            <Box className="mx-auto w-full max-w-[399px]" flex={false} gap={'20px'}>
              <Box>
                <Text margin={{ bottom: '2px' }} style={{ fontSize: '14px', fontWeight: 500 }}>
                  Bed
                </Text>
                <DropButton
                  dropContent={
                    <Box flex={false}>
                      {bedTypes?.map(item => (
                        <Box
                          key={item}
                          onClick={() => {
                            setValue('bedType', item as NonNullable<BedType | undefined>)
                            item !== 'Other' && setShowDrop(false)
                          }}
                          style={{
                            background: bed === item ? '#eeeeee' : 'none',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '10px 12px'
                          }}
                        >
                          {item}
                        </Box>
                      ))}
                      {bed === 'Other' && (
                        <Box gap={'12px'} margin={{ vertical: '16px' }} pad={{ horizontal: '14px' }}>
                          <Input onChange={e => setOtherText(e.target?.value as BedType)} value={otherText} />
                          {errors.otherText?.message && (
                            <Box pad={{ left: 'small' }}>
                              <Text color="status-error" size="small">
                                {errors.otherText?.message}
                              </Text>
                            </Box>
                          )}
                          <Box direction="row" justify="start">
                            <Button
                              icon={<Checkmark size="small" />}
                              label="Save"
                              onClick={() => {
                                setValue('bedType', otherText as NonNullable<BedType | undefined>)
                                setShowDrop(false)
                              }}
                              type="button"
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                  }
                  dropProps={{
                    align: { top: 'bottom' },
                    height: '400px',
                    margin: { left: '0px' },
                    style: { maxWidth: '400px' }
                  }}
                  onClose={() => setShowDrop(false)}
                  onOpen={() => setShowDrop(true)}
                  open={showDrop}
                  style={{
                    border: `1px solid rgba(0, 0, 0, 0.33)`,
                    borderRadius: '100px',
                    margin: '0',
                    padding: '10px 12px',
                    width: '100%'
                  }}
                >
                  <Box align="center" direction="row" flex justify="between" width={'100%'}>
                    <Box>
                      {bed && bed !== 'Other' ? (
                        <Text size="small" style={{ textTransform: 'capitalize' }} weight={500}>
                          {bed}
                        </Text>
                      ) : (
                        <Text color={'#aaaaaa'} size="small" weight={600}>
                          Select a bed type
                        </Text>
                      )}
                    </Box>
                    <FormDown color={palette.brand} />
                  </Box>
                </DropButton>
                {errors.bedType?.message && (
                  <Box pad={{ left: 'small' }}>
                    <Text color="status-error" size="small">
                      {errors.bedType?.message}
                    </Text>
                  </Box>
                )}
              </Box>
              <Box>
                <SelectInputField
                  dropProps={{
                    align: { left: 'left', right: 'right', top: 'bottom' }
                  }}
                  label="Status"
                  labelKey={'name'}
                  name="status"
                  options={bedStatus}
                  placeholder="Select status"
                  valueKey={{ key: 'value', reduce: true }}
                />
              </Box>
              <Controller
                control={control}
                name="telemetry"
                render={({ field: { onChange } }) => (
                  <Box direction="row" gap={'4px'}>
                    <CheckBox handleChange={onChange} />
                    <Text size="small">Telemetry</Text>
                  </Box>
                )}
              />
              <Box>
                <TextAreaField label="Description (optional)" name="description" />
              </Box>
            </Box>
          </Box>
          <FormButton isLoading={isSubmitting} onClose={handleClose} />
        </Form>
      </Modal>
    </FormProvider>
  )
}
